import { TextAreaInput, SelectTrigger } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { useCustomer } from "@jugl-web/domain-resources/customers/hooks/useCustomer/useCustomer";
import { useEffect, useMemo, useState } from "react";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { Customer } from "@jugl-web/rest-api";
import { customerToBillToString } from "@web-src/modules/invoices/utils/customerToBillToString";
import { CustomerPickerSidebar } from "@jugl-web/domain-resources/customers/components/CustomerPickerSidebar/CustomerPickerSidebar";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useInvoicesConfigList } from "../../../InvoicesSettingsPage/pages/InvoicesBusinessInformationPage/hooks/useInvoicesConfigList";
import { ReactComponent as ReloadIcon } from "./assets/reload.svg";

export const BillToForm = () => {
  const { t } = useTranslations();
  const { setFieldValue, formValues, register, errors } =
    useInvoicesItemFormPageContext();
  const { entity } = useEntitySelectedProvider();
  const [isSelectCustomerSidebarOpen, setIsSelectCustomerSidebarOpen] =
    useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const { isAdmin } = useUserRole();
  const [isChanged, setIsChanged] = useState(false);
  const { configId } = useInvoicesConfigList();

  const { customerDetails } = useCustomer({
    entityId: entity.id,
    customerId: formValues.customer_id,
  });

  const billToString = useMemo(() => {
    if (customerDetails) {
      return formValues.billTo;
    }

    return formValues.billTo;
  }, [customerDetails, formValues.billTo]);

  const refetchBillTo = (customer: Customer | null) => {
    const billTo = customerToBillToString(customer);
    setFieldValue("billTo", billTo);
  };

  useEffect(() => {
    if (customerDetails) {
      setSelectedCustomer(customerDetails as unknown as Customer);
    }
  }, [customerDetails]);

  return (
    <>
      <div className="space-y-8">
        <SelectTrigger
          isOpen={isSelectCustomerSidebarOpen}
          className="max-w-sm"
          label={
            selectedCustomer
              ? customerDetails?.fullName
              : t({
                  id: "customer-picker.select-customer",
                  defaultMessage: "Select customer",
                })
          }
          hasValue={Boolean(selectedCustomer?.id)}
          onClick={() => setIsSelectCustomerSidebarOpen(true)}
        />
        <CustomerPickerSidebar
          entityId={entity.id}
          isOpen={isSelectCustomerSidebarOpen}
          header={
            <div className="flex items-center gap-3 font-secondary">
              <div className="w-[300px] truncate text-sm font-semibold text-[#383838]">
                {t({
                  id: "tasks-page.select-customer",
                  defaultMessage: "Select Customer",
                })}
              </div>
            </div>
          }
          onSave={(selectedIds, selectedCustomers) => {
            const customer = selectedCustomers[0];
            setSelectedCustomer(customer);
            setFieldValue("customer_id", customer.id);
            refetchBillTo(customer);
            setIsSelectCustomerSidebarOpen(false);
          }}
          onClose={() => {
            setIsSelectCustomerSidebarOpen(false);
          }}
          defaultIds={formValues.customer_id ? [formValues.customer_id] : []}
          isAdmin={isAdmin}
          onAddNewCustomerSuccess={(customer: Customer) => {
            setSelectedCustomer(customer);
            setFieldValue("customer_id", customer.id);
            refetchBillTo(customer);
          }}
        />
        {isChanged && configId ? (
          <div className="relative z-10 w-full max-w-xl">
            <button
              type="button"
              className="absolute right-1 -bottom-9 translate-y-full transform cursor-pointer border-none bg-transparent p-0"
              onClick={() => {
                if (selectedCustomer) {
                  refetchBillTo(selectedCustomer);
                }
                setIsChanged(false);
              }}
            >
              <ReloadIcon />
            </button>
          </div>
        ) : null}
        <TextAreaInput
          isRequired
          errorMessage={errors.billTo?.message}
          isInvalid={!!errors.billTo}
          label={t({
            id: "invoices.item.billTo",
            defaultMessage: "Bill to",
          })}
          value={billToString}
          {...register("billTo", {
            onChange: () => {
              if (selectedCustomer) setIsChanged(true);
            },
            validate: (value) =>
              !value.trim() ? "Mandatory field" : undefined,
          })}
          classNames={{
            inputWrapperClassName: "h-40 max-w-xl",
            inputClassName:
              "min-h-full max-h-full jugl__custom-scrollbar overflow-y-auto",
          }}
        />
      </div>
    </>
  );
};
